import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { AdminServerResourceSamplesService } from '../../../api/api-services/admin-server-resource-samples.service';
import { IServerResourceSampleVm } from '../../../api/server-resource-sample-vm.model';

interface ISummaryCard {
  label: string;
  icon: string;
  value: string;
  detail: string;
}

interface IServerSummaryGroup {
  serverName: string;
  lastSampleUtc: string;
  cards: ISummaryCard[];
}

const FALLBACK_SERVER = 'serwer';

// Per-metric palettes indexed by server — two containers sharing one database
// get distinguishable line colors within the same chart.
const CPU_COLORS = ['#3b82f6', '#06b6d4', '#6366f1'];
const RAM_COLORS = ['#10b981', '#84cc16', '#14b8a6'];
const DISK_COLORS = ['#f59e0b', '#f97316', '#eab308'];
const TX_COLORS = ['#ef4444', '#f43f5e', '#dc2626'];
const RX_COLORS = ['#8b5cf6', '#a855f7', '#7c3aed'];

@Component({
  selector: 'app-server-status',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ChartModule,
    ProgressSpinnerModule,
    TooltipModule,
  ],
  templateUrl: './server-status.component.html',
  styleUrl: './server-status.component.scss',
})
export class ServerStatusComponent implements OnInit, OnDestroy {
  private _samplesService = inject(AdminServerResourceSamplesService);

  historyHours = 6;
  hoursOptions = [1, 6, 24, 72];
  loading = false;

  cpuChartData: any = null;
  ramChartData: any = null;
  diskChartData: any = null;
  networkChartData: any = null;
  percentChartOptions: any = this.buildChartOptions('%', 100);
  networkChartOptions: any = this.buildChartOptions(' MB/s');

  summaryGroups: IServerSummaryGroup[] = [];
  lastSampleUtc: string | null = null;

  private _samples: IServerResourceSampleVm[] = [];
  private _refreshTimer: any = null;
  // Monotonic request guard — a stale response (older request arriving late) must
  // never overwrite the state of a newer one (hours switch, slow 72h query).
  private _reqSeq = 0;

  ngOnInit(): void {
    this.loadSamples();
    this._refreshTimer = setInterval(() => this.loadSamples(), 60000);
  }

  ngOnDestroy(): void {
    if (this._refreshTimer) clearInterval(this._refreshTimer);
  }

  onHoursChange(hours: number): void {
    this.historyHours = hours;
    this.loadSamples();
  }

  get hasAnyChart(): boolean {
    return !!(
      this.cpuChartData ||
      this.ramChartData ||
      this.diskChartData ||
      this.networkChartData
    );
  }

  private loadSamples(): void {
    const seq = ++this._reqSeq;
    this.loading = true;
    this._samplesService.get(this.historyHours).subscribe({
      next: (samples) => {
        if (seq !== this._reqSeq) return;
        this._samples = samples ?? [];
        this.rebuildCharts();
        this.rebuildSummary();
        this.loading = false;
      },
      error: () => {
        if (seq !== this._reqSeq) return;
        this.loading = false;
      },
    });
  }

  // ---- charts ----

  private serverOf(sample: IServerResourceSampleVm): string {
    return sample.serverName || FALLBACK_SERVER;
  }

  private rebuildCharts(): void {
    const samples = this._samples;
    if (!samples.length) {
      this.cpuChartData = null;
      this.ramChartData = null;
      this.diskChartData = null;
      this.networkChartData = null;
      return;
    }

    const servers = Array.from(new Set(samples.map((s) => this.serverOf(s))));
    const multi = servers.length > 1;

    // Instances sample on their own 30s clocks — the shared X axis is the union of
    // all timestamps, and each server's series has nulls where it has no sample
    // (spanGaps draws through them).
    const timestamps = Array.from(
      new Set(samples.map((s) => s.timestampUtc)),
    ).sort();
    const labels = timestamps.map((t) => new Date(t).toLocaleTimeString());

    const byServer = new Map<string, Map<string, IServerResourceSampleVm>>();
    for (const sample of samples) {
      const server = this.serverOf(sample);
      if (!byServer.has(server)) byServer.set(server, new Map());
      byServer.get(server)!.set(sample.timestampUtc, sample);
    }

    const line = (
      label: string,
      color: string,
      server: string,
      valueOf: (s: IServerResourceSampleVm) => number | null,
    ) => ({
      label,
      data: timestamps.map((t) => {
        const sample = byServer.get(server)!.get(t);
        return sample ? valueOf(sample) : null;
      }),
      borderColor: color,
      backgroundColor: color + '33',
      tension: 0.3,
      spanGaps: true,
      fill: !multi,
      pointRadius: 0,
    });

    const serverHasValue = (
      server: string,
      valueOf: (s: IServerResourceSampleVm) => number | null,
    ) =>
      Array.from(byServer.get(server)!.values()).some(
        (s) => valueOf(s) !== null,
      );

    const buildChart = (
      metricLabel: string,
      colors: string[],
      valueOf: (s: IServerResourceSampleVm) => number | null,
    ) => {
      const datasets = servers
        .filter((server) => serverHasValue(server, valueOf))
        .map((server, index) =>
          line(
            multi ? `${server} — ${metricLabel}` : metricLabel,
            colors[index % colors.length],
            server,
            valueOf,
          ),
        );
      return datasets.length ? { labels, datasets } : null;
    };

    const cpu = (s: IServerResourceSampleVm) => s.cpuUsagePercent ?? null;
    const ram = (s: IServerResourceSampleVm) => s.ramUsedPercent ?? null;
    const disk = (s: IServerResourceSampleVm) => s.diskUsedPercent ?? null;
    const toMbPerSec = (bytes?: number) =>
      bytes === undefined || bytes === null
        ? null
        : Math.round((bytes / (1024 * 1024)) * 100) / 100;
    const tx = (s: IServerResourceSampleVm) => toMbPerSec(s.networkTxBytesPerSec);
    const rx = (s: IServerResourceSampleVm) => toMbPerSec(s.networkRxBytesPerSec);

    this.cpuChartData = buildChart('CPU [%]', CPU_COLORS, cpu);
    this.ramChartData = buildChart('RAM [%]', RAM_COLORS, ram);
    this.diskChartData = buildChart('Dysk [%]', DISK_COLORS, disk);

    const networkDatasets = servers.flatMap((server, index) => {
      const prefix = multi ? `${server} — ` : '';
      const sets: any[] = [];
      if (serverHasValue(server, tx)) {
        sets.push(
          line(
            `${prefix}Wysyłanie (TX) [MB/s]`,
            TX_COLORS[index % TX_COLORS.length],
            server,
            tx,
          ),
        );
      }
      if (serverHasValue(server, rx)) {
        sets.push(
          line(
            `${prefix}Pobieranie (RX) [MB/s]`,
            RX_COLORS[index % RX_COLORS.length],
            server,
            rx,
          ),
        );
      }
      return sets;
    });
    this.networkChartData = networkDatasets.length
      ? { labels, datasets: networkDatasets }
      : null;
  }

  private buildChartOptions(unit: string, max?: number): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        y: {
          min: 0,
          ...(max ? { max } : {}),
          ticks: { callback: (v: any) => v + unit },
        },
        x: { ticks: { maxTicksLimit: 12 } },
      },
      plugins: { legend: { position: 'bottom' } },
    };
  }

  // ---- summary cards ----

  private rebuildSummary(): void {
    if (!this._samples.length) {
      this.summaryGroups = [];
      this.lastSampleUtc = null;
      return;
    }

    const servers = Array.from(
      new Set(this._samples.map((s) => this.serverOf(s))),
    );

    this.summaryGroups = servers.map((server) => {
      const serverSamples = this._samples.filter(
        (s) => this.serverOf(s) === server,
      );
      const latest = serverSamples[serverSamples.length - 1];
      return {
        serverName: server,
        lastSampleUtc: latest.timestampUtc,
        cards: this.buildCards(latest),
      };
    });

    this.lastSampleUtc = this.summaryGroups
      .map((g) => g.lastSampleUtc)
      .sort()
      .pop()!;
  }

  private buildCards(latest: IServerResourceSampleVm): ISummaryCard[] {
    return [
      {
        label: 'CPU',
        icon: 'pi pi-microchip',
        value: this.formatPercent(latest.cpuUsagePercent),
        detail: 'obciążenie procesorów',
      },
      {
        label: 'RAM',
        icon: 'pi pi-server',
        value: this.formatPercent(latest.ramUsedPercent),
        detail: this.formatUsage(latest.ramUsedMb, latest.ramTotalMb),
      },
      {
        label: 'Dysk',
        icon: 'pi pi-database',
        value: this.formatPercent(latest.diskUsedPercent),
        detail: this.formatUsage(latest.diskUsedMb, latest.diskTotalMb),
      },
      {
        label: 'Sieć',
        icon: 'pi pi-arrow-right-arrow-left',
        value: `↑ ${this.formatRate(latest.networkTxBytesPerSec)}`,
        detail: `↓ ${this.formatRate(latest.networkRxBytesPerSec)}`,
      },
    ];
  }

  private formatPercent(value?: number): string {
    return value === undefined || value === null ? '—' : `${value.toFixed(1)}%`;
  }

  private formatUsage(usedMb?: number, totalMb?: number): string {
    if (usedMb === undefined || usedMb === null || !totalMb) return '';
    return `${this.formatMb(usedMb)} / ${this.formatMb(totalMb)}`;
  }

  private formatMb(mb: number): string {
    return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
  }

  private formatRate(bytesPerSec?: number): string {
    if (bytesPerSec === undefined || bytesPerSec === null) return '—';
    if (bytesPerSec < 1024) return `${bytesPerSec.toFixed(0)} B/s`;
    if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(1)} KB/s`;
    return `${(bytesPerSec / (1024 * 1024)).toFixed(2)} MB/s`;
  }
}
